import { useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { editJob, getJobById } from '../Services/Jobs';
import toast from 'react-hot-toast';
import { t } from 'i18next';
import { SENIORITY_OPTIONS } from '../Types/constants';
import { useJobStore } from '../Stores/useGeneralAppStore';

export const useJob: () => any = () => {
  const { jobId } = useParams();
  const queryClient = useQueryClient();
  const [selectedTab, setSelectedTab] = useState(0);
  const [job, setJob] = useState<Job>();
  const [hasBeenEdited, setHasBeenEdited] = useState(false);
  const { setSelectedStoreJobId } = useJobStore();

  const { data: jobData } = useQuery({
    queryKey: ['getJobById', jobId],
    queryFn: () => getJobById(jobId),
  });

  useEffect(() => {
    if (jobData) {

      const updatedJobData = { ...jobData };

      if (updatedJobData.seniority) {
        updatedJobData.seniority =
          SENIORITY_OPTIONS[updatedJobData.seniority] || updatedJobData.seniority;
      }
      setJob(updatedJobData);
      setSelectedStoreJobId(updatedJobData.id);
      setHasBeenEdited(false);
    }
  }, [jobData]);

  const updateJob = (updatedFields: Job) => {
    setJob((prevJob) => {
      if (!prevJob) return prevJob;
      setHasBeenEdited(true);

      const updatedJob: Job = { ...prevJob };
      updatedJob.position = updatedFields.position ?? prevJob.position;
      updatedJob.jobReference = updatedFields.jobReference ?? prevJob.jobReference;
      updatedJob.headcount = updatedFields.headcount ?? prevJob.headcount;
      updatedJob.clientId = updatedFields.client?.id ?? prevJob.clientId;
      updatedJob.departmentId = updatedFields.jobDepartment?.id ?? prevJob.departmentId;
      updatedJob.requestedDateTime = updatedFields.requestedDateTime && updatedFields.requestedDateTime !== 'undefined' ? updatedFields.requestedDateTime : prevJob.requestedDateTime && prevJob.requestedDateTime !== 'undefined' ? prevJob.requestedDateTime : '';
      updatedJob.requester = updatedFields.requester ?? prevJob.requester;
      updatedJob.techHub = updatedFields.techHub ?? prevJob.techHub;
      updatedJob.seniority = updatedFields.seniority ?? prevJob.seniority;
      updatedJob.experience = updatedFields.experience ?? prevJob.experience;
      updatedJob.minimumSalary = updatedFields.minimumSalary ?? prevJob.minimumSalary;
      updatedJob.maximumSalary = updatedFields.maximumSalary ?? prevJob.maximumSalary;
      updatedJob.currency = updatedFields.currency ?? prevJob.currency;
      updatedJob.expectedStartDateTime = updatedFields.expectedStartDateTime && updatedFields.expectedStartDateTime !== 'undefined' ? updatedFields.expectedStartDateTime : prevJob.expectedStartDateTime && prevJob.expectedStartDateTime !== 'undefined' ? prevJob.expectedStartDateTime : '';
      updatedJob.market = updatedFields.market ?? prevJob.market;
      updatedJob.modality = updatedFields.modality ?? prevJob.modality;
      updatedJob.characteristics = updatedFields.characteristics ?? prevJob.characteristics;
      updatedJob.confidential = updatedFields.confidential ?? prevJob.confidential;
      updatedJob.priority = updatedFields.priority ?? prevJob.priority;
      updatedJob.description = updatedFields.description ?? prevJob.description;

      return updatedJob;
    });
  };

  const { mutate: editJobMutation } = useMutation({
    mutationFn: (data: Job) => {
      const updatedData = { ...data };
      return editJob(job?.id, updatedData);
    },
    onError: (_error: any) => {
      toast.error(t('toast.errors.something_went_wrong'));
    },
    onSuccess: (data: { id: string; }) => {
      toast.success(t('toast.success.edit', { elem: 'Job ' + data.id }));
      queryClient.invalidateQueries({ queryKey: ['getJobById'] });
    },
  });

  useEffect(() => {
    if (job && hasBeenEdited) {
      const updatedJob = { ...job };
      Object.keys(updatedJob).forEach((key) => {
        if (updatedJob[key] === '') {
          updatedJob[key] = null;
        }
      });

      editJobMutation(updatedJob);
    }
  }, [job, hasBeenEdited, editJobMutation]);

  const handleChange = (_event: React.SyntheticEvent, newValue: number) => {
    setSelectedTab(newValue);
  };

  return {
    job,
    selectedTab,
    handleChange,
    updateJob,
  };
};
